import ShipsService from "@/services/ships";
import { IDBPDatabase } from "idb/build/esm/entry";
import IShip from "@/models/ship";
import DataService from "@/services/data-service/data-service";
import VmsDbSchema from "@/models/VmsDbSchema";
import groupBy from "@/plugins/group-by";
import IMaritimeStandardsResponse from "@/models/IMaritimeStandardsResponse";

export default class DataShipService {
  private shipService = new ShipsService();

  public actions = {
    Get: async (forceOnline = false, fullShip = false) =>
      (await this.Get(-1, forceOnline, fullShip)) as IShip[],
    GetById: async (id: number, forceOnline = false, fullShip = false) =>
      (await this.Get(id, forceOnline, fullShip)) as IShip,
    Delete: this.shipService.delete,
    GetShipAreas: this.shipService.GetShipAreas,
    GetMachineryCompartmentsByShipAreaId: this.shipService.GetMachineryCompartmentsByShipAreaId,
    GetMaritimeStandards: this.GetMaritimeStandards.bind(this),
    Update: this.shipService.update
  };

  constructor(private $db: Promise<IDBPDatabase<VmsDbSchema>>, private $dataService: DataService) {}
  public async initialize() {
    const ships = await this.actions.Get();
    const shipsAll = await Promise.all(
      ships.map(s => this.actions.GetById(s.id as number, undefined, true))
    );
    await this.GetMaritimeStandards();
    return [ships, ...shipsAll];
  }
  private async Get(id = -1, forceOnline = false, fullShip = false) {
    if (!this.$dataService.getters.isOfflineSupport())
      return id == -1
        ? (await this.shipService.getShips())?.data
        : fullShip
        ? await this.shipService.getByIdFull(id)
        : await this.shipService.getById(id);
    if (forceOnline) return await this.setShips(id);
    return (await this.getShipsFromDb(id)) ?? (await this.setShips(id));
  }

  private async GetMaritimeStandards(shipId?: number, forceOnline = false) {
    if (!this.$dataService.getters.isOfflineSupport())
      return await this.shipService.getMaritimeStandards(shipId);
    if (forceOnline) return await this.setMaritimeStandards(shipId);
    return (
      (await this.getMaritimeStandardsFromDb(shipId)) ?? (await this.setMaritimeStandards(shipId))
    );
  }

  private async setMaritimeStandards(shipId?: number) {
    const maritimeData = await this.shipService.getMaritimeStandards(shipId);
    const groupedMaritimeData = groupBy(maritimeData, "shipID") as {
      [key: string]: IMaritimeStandardsResponse;
    };
    for (const key of Object.keys(groupedMaritimeData)) {
      const encryptShips = DataService.encryptData(groupedMaritimeData[key]);
      await (await this.$db).put("MaritimeStandards", encryptShips, parseInt(key));
    }
    return maritimeData;
  }

  private async getMaritimeStandardsFromDb(shipId?: number) {
    const encryptedData = shipId
      ? await (await this.$db).get("MaritimeStandards", shipId)
      : await (await this.$db).getAll("MaritimeStandards");
    if (!encryptedData || encryptedData.length === 0) return null;
    return shipId
      ? DataService.decryptData<IMaritimeStandardsResponse[]>(encryptedData as string)
      : (encryptedData as string[])
          .map(d => DataService.decryptData<IMaritimeStandardsResponse[]>(d))
          .flat();
  }

  private async getShipsFromDb(id = -1) {
    const encryptedData = await (await this.$db).get("Ships", id);
    if (!encryptedData) return null;
    return DataService.decryptData<IShip[] | IShip>(encryptedData);
  }
  private async setShips(id = -1) {
    const ships =
      id === -1
        ? (await this.shipService.getShips())?.data
        : await this.shipService.getByIdFull(id);
    const encryptShips = DataService.encryptData(ships);
    await (await this.$db).put("Ships", encryptShips, id);
    return ships;
  }
}
